import React, { useState, useEffect, useMemo } from 'react';
import {
  Layers,
  Plus,
  Search,
  CheckCircle2,
  XCircle,
  Edit2,
  Filter,
  RefreshCw,
  Sparkles,
  ArrowRight,
  Shield,
  Tag,
  Hash,
} from 'lucide-react';
import { CategoryIcon } from '../common/CategoryIcon';
import type { ServiceCategory, ServiceSubcategory } from '../../types';
import { getAdminHeaders } from '../../lib/supabaseService';

export const AdminCategorySection: React.FC = () => {
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);

  // Modal States
  const [showAddCategoryModal, setShowAddCategoryModal] = useState<boolean>(false);
  const [showAddSubcategoryModal, setShowAddSubcategoryModal] = useState<boolean>(false);
  const [targetCategoryForSub, setTargetCategoryForSub] = useState<string>('');

  // Form States for New Category
  const [newCatName, setNewCatName] = useState('');
  const [newCatSlug, setNewCatSlug] = useState('');
  const [newCatDesc, setNewCatDesc] = useState('');
  const [newCatIcon, setNewCatIcon] = useState('Wrench');
  const [newCatPriceRange, setNewCatPriceRange] = useState('₦10,000 - ₦80,000');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form States for New Subcategory
  const [newSubName, setNewSubName] = useState('');
  const [newSubSlug, setNewSubSlug] = useState('');
  const [newSubDesc, setNewSubDesc] = useState('');

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const headers = await getAdminHeaders();
      const res = await fetch('/api/categories?include_inactive=true', {
        headers,
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.categories)) {
        setCategories(data.categories);
      }
    } catch (err) {
      console.warn('Failed to fetch categories:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const totalSubcategoriesCount = useMemo(() => {
    return categories.reduce((acc, cat) => acc + (cat.subcategories?.length || 0), 0);
  }, [categories]);

  const activeCategoriesCount = useMemo(() => {
    return categories.filter((c) => c.isActive !== false).length;
  }, [categories]);

  const filteredCategories = useMemo(() => {
    return categories.filter((cat) => {
      if (statusFilter === 'active' && cat.isActive === false) return false;
      if (statusFilter === 'inactive' && cat.isActive !== false) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = cat.name.toLowerCase().includes(q);
        const matchesSlug = cat.slug.toLowerCase().includes(q);
        const matchesSub = cat.subcategories?.some((s) => s.name.toLowerCase().includes(q) || s.slug.includes(q));
        if (!matchesName && !matchesSlug && !matchesSub) return false;
      }
      return true;
    });
  }, [categories, statusFilter, searchQuery]);

  const handleToggleCategoryActive = async (cat: ServiceCategory) => {
    const updatedStatus = !cat.isActive;
    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch(`/api/categories/${cat.slug}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ isActive: updatedStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setCategories((prev) =>
          prev.map((c) => (c.slug === cat.slug ? { ...c, isActive: updatedStatus } : c))
        );
        setActionMessage({
          type: 'success',
          text: `Category '${cat.name}' is now ${updatedStatus ? 'Active' : 'Inactive'}.`,
        });
      }
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err?.message || 'Failed to update category status.' });
    }
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCatName.trim() || !newCatSlug.trim()) return;

    setIsSubmitting(true);
    setActionMessage(null);

    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/categories', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: newCatName.trim(),
          slug: newCatSlug.trim().toLowerCase(),
          description: newCatDesc.trim(),
          iconName: newCatIcon,
          typicalPriceRange: newCatPriceRange.trim(),
          isActive: true,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setActionMessage({ type: 'success', text: `Category '${newCatName}' created successfully.` });
        setShowAddCategoryModal(false);
        setNewCatName('');
        setNewCatSlug('');
        setNewCatDesc('');
        fetchCategories();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to create category.' });
      }
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err?.message || 'Network error.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubcategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetCategoryForSub || !newSubName.trim() || !newSubSlug.trim()) return;

    setIsSubmitting(true);
    setActionMessage(null);

    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/subcategories', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          categorySlug: targetCategoryForSub,
          name: newSubName.trim(),
          slug: newSubSlug.trim().toLowerCase(),
          description: newSubDesc.trim(),
          isActive: true,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setActionMessage({ type: 'success', text: `Subcategory '${newSubName}' added successfully.` });
        setShowAddSubcategoryModal(false);
        setNewSubName('');
        setNewSubSlug('');
        setNewSubDesc('');
        fetchCategories();
      } else {
        setActionMessage({ type: 'error', text: data.error || 'Failed to create subcategory.' });
      }
    } catch (err: any) {
      setActionMessage({ type: 'error', text: err?.message || 'Network error.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Overview Card */}
      <div className="bg-slate-900 text-white rounded-3xl p-6 border border-slate-800 shadow-xl relative overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
              <Layers className="w-3.5 h-3.5" />
              <span>Service Taxonomy & Category Management</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white">
              Nigerian Skilled Trades & Subcategories Directory
            </h2>
            <p className="text-xs text-slate-300">
              Manage all 24 major Nigerian service trades, add subcategories, and control marketplace availability.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchCategories}
              className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={() => setShowAddCategoryModal(true)}
              className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Add Category</span>
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-6 border-t border-slate-800 text-left">
          <div className="bg-slate-800/60 rounded-2xl p-3 border border-slate-700/60">
            <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block">Total Categories</span>
            <span className="text-xl font-black text-white">{categories.length}</span>
          </div>
          <div className="bg-slate-800/60 rounded-2xl p-3 border border-slate-700/60">
            <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block">Active Categories</span>
            <span className="text-xl font-black text-emerald-400">{activeCategoriesCount}</span>
          </div>
          <div className="bg-slate-800/60 rounded-2xl p-3 border border-slate-700/60">
            <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block">Total Subcategories</span>
            <span className="text-xl font-black text-teal-300">{totalSubcategoriesCount}</span>
          </div>
          <div className="bg-slate-800/60 rounded-2xl p-3 border border-slate-700/60">
            <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block">Nigerian States</span>
            <span className="text-xl font-black text-sky-400">36 + FCT</span>
          </div>
        </div>
      </div>

      {/* Action Notification Alert */}
      {actionMessage && (
        <div
          className={`p-3 rounded-2xl text-xs font-semibold flex items-center justify-between ${
            actionMessage.type === 'success'
              ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
              : 'bg-rose-50 text-rose-900 border border-rose-200'
          }`}
        >
          <span>{actionMessage.text}</span>
          <button onClick={() => setActionMessage(null)} className="text-slate-500 hover:text-slate-800 cursor-pointer">
            ✕
          </button>
        </div>
      )}

      {/* Search & Filter Bar */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            placeholder="Search categories or subcategories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold cursor-pointer ${
              statusFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All ({categories.length})
          </button>
          <button
            onClick={() => setStatusFilter('active')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold cursor-pointer ${
              statusFilter === 'active' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Active ({activeCategoriesCount})
          </button>
          <button
            onClick={() => setStatusFilter('inactive')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold cursor-pointer ${
              statusFilter === 'inactive' ? 'bg-rose-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Inactive ({categories.length - activeCategoriesCount})
          </button>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCategories.map((cat) => {
          const isActive = cat.isActive !== false;
          return (
            <div
              key={cat.slug}
              className={`bg-white rounded-3xl border transition-all p-5 flex flex-col justify-between space-y-4 shadow-xs hover:shadow-md ${
                isActive ? 'border-slate-200' : 'border-rose-200 bg-rose-50/20'
              }`}
            >
              <div className="space-y-3">
                {/* Top Row: Icon, Name, Active Toggle */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-100 shrink-0">
                      <CategoryIcon slug={cat.slug} className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-extrabold text-sm text-slate-900">{cat.name}</h3>
                      <span className="text-[11px] font-mono text-slate-400 block">slug: {cat.slug}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleToggleCategoryActive(cat)}
                    title={`Click to ${isActive ? 'Deactivate' : 'Activate'}`}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold cursor-pointer transition-colors ${
                      isActive
                        ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                        : 'bg-rose-100 text-rose-800 hover:bg-rose-200'
                    }`}
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </button>
                </div>

                {/* Description */}
                <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">{cat.description}</p>

                {/* Price Range */}
                <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
                  <span>Typical Rate:</span>
                  <strong className="text-slate-800 font-semibold">{cat.typicalPriceRange || '₦10,000 - ₦80,000'}</strong>
                </div>

                {/* Subcategories List */}
                <div className="space-y-1 pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between text-[11px] text-slate-500 font-bold uppercase tracking-wider">
                    <span>Subcategories ({cat.subcategories?.length || 0})</span>
                    <button
                      onClick={() => {
                        setTargetCategoryForSub(cat.slug);
                        setShowAddSubcategoryModal(true);
                      }}
                      className="text-emerald-600 hover:text-emerald-700 flex items-center gap-0.5 cursor-pointer"
                    >
                      <Plus className="w-3 h-3" />
                      <span>Add</span>
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto p-1 bg-slate-50 rounded-xl">
                    {cat.subcategories && cat.subcategories.length > 0 ? (
                      cat.subcategories.map((sub) => (
                        <span
                          key={sub.slug}
                          className="px-2 py-0.5 rounded text-[10px] bg-white border border-slate-200 text-slate-700 font-medium"
                        >
                          {sub.name}
                        </span>
                      ))
                    ) : (
                      <span className="text-[10px] text-slate-400 italic p-1">No subcategories defined.</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom Card Footer */}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                <span>Order #{cat.sortOrder || 1}</span>
                <span className="text-emerald-600 font-semibold">JobMatch Verified Trade</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* MODAL 1: ADD CATEGORY */}
      {showAddCategoryModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-black text-base text-slate-900">Add New Service Trade</h3>
              <button onClick={() => setShowAddCategoryModal(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Category Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Solar Technician"
                  value={newCatName}
                  onChange={(e) => {
                    setNewCatName(e.target.value);
                    if (!newCatSlug) {
                      setNewCatSlug(e.target.value.toLowerCase().trim().replace(/[^a-z0-9]/g, '-'));
                    }
                  }}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Unique Slug</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. solar-technician"
                  value={newCatSlug}
                  onChange={(e) => setNewCatSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  placeholder="Brief description of skilled works covered..."
                  value={newCatDesc}
                  onChange={(e) => setNewCatDesc(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Typical Price Range (₦)</label>
                <input
                  type="text"
                  placeholder="₦15,000 - ₦120,000"
                  value={newCatPriceRange}
                  onChange={(e) => setNewCatPriceRange(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddCategoryModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? 'Creating...' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: ADD SUBCATEGORY */}
      {showAddSubcategoryModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-black text-base text-slate-900">Add Subcategory</h3>
                <span className="text-xs text-slate-500">Under '{targetCategoryForSub}'</span>
              </div>
              <button onClick={() => setShowAddSubcategoryModal(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateSubcategory} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Subcategory Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Inverter Wiring & Setup"
                  value={newSubName}
                  onChange={(e) => {
                    setNewSubName(e.target.value);
                    if (!newSubSlug) {
                      setNewSubSlug(e.target.value.toLowerCase().trim().replace(/[^a-z0-9]/g, '-'));
                    }
                  }}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Subcategory Slug</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. inverter-wiring"
                  value={newSubSlug}
                  onChange={(e) => setNewSubSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Description (Optional)</label>
                <textarea
                  rows={2}
                  placeholder="Specific scope of work..."
                  value={newSubDesc}
                  onChange={(e) => setNewSubDesc(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddSubcategoryModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? 'Adding...' : 'Add Subcategory'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
